import type { DocumentsQuery } from "generated-graphql/graphql";

import { TypedDocumentNode, useQuery } from "@apollo/client";
import { useMemo } from "react";

import { DOCUMENTS_BY_IDS } from "../queries";

type DocumentListItem = Pick<
  DocumentsQuery["documents"]["items"][number],
  "id" | "title" | "documentType" | "extractionStatus" | "documentMetadata"
>;

type ReviewDocumentsQueryResult = {
  documents: {
    items: DocumentListItem[];
  };
};

type ReviewDocumentsQueryVariables = {
  search: string;
  pageSize?: number;
};

type UseReviewDocumentsListParams = {
  documentIds: string[];
  tenantId?: string;
  skip?: boolean;
};

type UseReviewDocumentsListResult = {
  documents: Array<DocumentListItem | undefined>;
  documentsById: Record<string, DocumentListItem>;
  loading: boolean;
};

export function useReviewDocumentsList({
  documentIds,
  tenantId,
  skip = false,
}: UseReviewDocumentsListParams): UseReviewDocumentsListResult {
  const search = useMemo(() => {
    const uniqueIds = Array.from(
      new Set(documentIds.filter((id) => typeof id === "string" && id.length))
    );

    if (uniqueIds.length === 0) {
      return undefined;
    }

    const parts = [`id:${uniqueIds.join("|")}`];

    if (tenantId) {
      parts.unshift(`tenantId:${tenantId}`);
    }

    return parts.join(" ");
  }, [documentIds, tenantId]);

  const { data, loading } = useQuery<ReviewDocumentsQueryResult, ReviewDocumentsQueryVariables>(
    DOCUMENTS_BY_IDS as TypedDocumentNode<
      ReviewDocumentsQueryResult,
      ReviewDocumentsQueryVariables
    >,
    {
      variables: {
        search: search ?? "",
        pageSize: documentIds.length || undefined,
      },
      skip: skip || !search,
      fetchPolicy: "cache-first",
    }
  );

  const documentsById = useMemo(() => {
    const items = data?.documents.items ?? [];
    return items.reduce<Record<string, DocumentListItem>>((acc, doc) => {
      acc[doc.id] = doc;
      return acc;
    }, {});
  }, [data]);

  const documents = useMemo(
    () => documentIds.map((id) => documentsById[id]),
    [documentIds, documentsById]
  );

  return {
    documents,
    documentsById,
    loading,
  };
}
